import React, { useCallback } from 'react';
import { FormattedNumber } from 'react-intl';
import { useObservable } from 'react-use-observable';

import { Checkbox, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { formatDate } from 'helpers/date';
import type { IOrder } from 'v2/components/Pages/Orders/interfaces';
import IconPaymentMethodProps from 'v2/components/Shared/IconPaymentMethod';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import { TableActionButton } from 'v2/components/Shared/TableActionButton';
import ordersService from 'v2/services/orders';

const List = () => {
  useObservable(() => ordersService.load(), []);
  const [isLoading] = useObservable(() => ordersService.isLoading(), []);
  const [orders] = useObservable(() => ordersService.getOrders(), []);
  const [pagination] = useObservable(() => ordersService.getPaginationResponse(), []);

  const ordersChecked = (orders || []).filter(o => o.checked);

  const openQuickView = useCallback((order: IOrder) => ordersService.openQuickview(order).subscribe(), []);

  const onCheckAll = useCallback(() => ordersService.checkAllOrders(), []);

  const onUnCheckAll = useCallback(() => ordersService.unCheckAllOrders(), []);

  const onCheck = useCallback((invoiceId: number) => ordersService.checkOrder(invoiceId), []);

  const scrollToTable = () => {
    document.getElementById('orders_table')?.scrollIntoView();
  };

  const onChangeRowsPerPage = (perPage: number) => {
    ordersService.setPagination({ ...pagination, per_page: perPage, current_page: 1 });
    scrollToTable();
  };

  const onChangePage = (page: number) => {
    ordersService.setPagination({ ...pagination, current_page: page });
    scrollToTable();
  };

  const currentIndex = pagination ? (pagination.current_page - 1) * pagination.per_page + 1 : 1;

  const columns: ColumnsType<IOrder> = [
    {
      title: '#',
      width: 20,
      render: (_: unknown, _order: IOrder, index: number) => <span>{currentIndex + index}.</span>
    },
    {
      title: () => (
        <PermissionHide action='myeduzz:minhas-entregas:editar'>
          <Checkbox
            indeterminate={!!ordersChecked.length && orders?.length !== ordersChecked.length}
            checked={orders?.length === ordersChecked.length && !!orders?.length}
            onChange={orders?.length === ordersChecked.length ? onUnCheckAll : onCheckAll}
          />
        </PermissionHide>
      ),
      width: 45,
      render: (_: unknown, order: IOrder) => (
        <PermissionHide action='myeduzz:minhas-entregas:editar'>
          <Checkbox checked={order.checked === true} onChange={() => onCheck(order.invoice_id)} />
        </PermissionHide>
      )
    },
    {
      title: 'Fatura',
      width: 110,
      render: (_: unknown, order: IOrder) => (
        <div className={'max-sm:flex max-sm:flex-col max-sm:[&_p]:mr-2'}>
          <span className='font-semibold'>{order.invoice_id}</span>
          <br />
          <span className={'max-w-[100px] font-extrabold uppercase'}>{order.payment_status?.name || ''}</span>
        </div>
      )
    },
    {
      title: 'Produto',
      width: 250,
      render: (_: unknown, order: IOrder) => <span>{order.product.title}</span>
    },
    {
      title: 'Entrega',
      width: 170,
      render: (_: unknown, order: IOrder) => <span className='font-semibold'>{order.status}</span>
    },
    {
      title: 'Cliente',
      render: (_: unknown, order: IOrder) => (
        <span className='font-semibold'>
          <PermissionHide action='myeduzz:minhas-entregas:ver-dados-clientes' fallback={<em>oculto</em>}>
            {order.customer.name}
          </PermissionHide>
        </span>
      )
    },
    {
      title: 'Data',
      width: 100,
      render: (_: unknown, order: IOrder) => <span>{formatDate(order.created_at)}</span>
    },
    {
      title: 'Frete',
      width: 150,
      align: 'right',
      render: (_: unknown, order: IOrder) => (
        <PermissionHide action='myeduzz:minhas-entregas:ocultar-valores' inverse fallback={<em>oculto</em>}>
          <span className='font-semibold'>
            <FormattedNumber format='BRL' value={order.freight_value} />
          </span>
          <br />
          <span>{order.delivery_type_shortened}</span>
        </PermissionHide>
      )
    },
    {
      title: 'Ganho',
      width: 120,
      align: 'right',
      render: (_: unknown, order: IOrder) => (
        <PermissionHide action='myeduzz:minhas-entregas:ocultar-valores' inverse fallback={<em>oculto</em>}>
          <span className='text-right font-semibold'>
            <FormattedNumber format='BRL' value={order.net_income} />
          </span>
          <IconPaymentMethodProps cardBrand={order.card_brand} />
        </PermissionHide>
      )
    },
    {
      title: '',
      width: 48,
      fixed: 'right',
      align: 'right',
      render: (_: unknown, order: IOrder) => <TableActionButton onClick={() => openQuickView(order)} />
    }
  ];

  return (
    <Table
      id='orders_table'
      loading={isLoading}
      columns={columns}
      dataSource={orders || []}
      rowKey='invoice_id'
      scroll={{ x: 'max-content' }}
      size='small'
      pagination={
        !isLoading && pagination
          ? {
              current: pagination.current_page || 1,
              pageSize: pagination.per_page || 10,
              total: pagination.total_items || 0,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100', '200'],
              onChange: page => onChangePage(page),
              onShowSizeChange: (_, size) => onChangeRowsPerPage(size)
            }
          : false
      }
    />
  );
};
export default List;
